'use client';

import {
  Button,
  Dialog,
  Field,
  Input,
  LabelDefault,
  MetaDefault,
  SegmentGroupHorizontal,
} from '@terra/ui-design-system';
import { HStack, VStack } from '@terra/ui-styled-system';
import {
  TERMINAL_APPROVAL_RULE_PATTERN_MAX_LENGTH,
  TerminalApprovalRuleActionSchema,
  TerminalApprovalRuleSchema,
  formatToolApprovalRule,
  type TerminalApprovalRule,
  type TerminalApprovalRuleAction,
} from '@terra/shared-dto-chat-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTerminalApprovalStore } from '../../store/terminalApprovalStore';
import { TerminalApprovalRuleRow } from './TerminalApprovalRuleRow';

type TerminalApprovalRulesDialogProps = {
  isOpen: boolean;
  onClose: () => void;
};

const ACTION_ORDER: TerminalApprovalRuleAction[] = ['allow', 'ask', 'deny'];

const ACTION_LABEL_KEYS: Record<
  TerminalApprovalRuleAction,
  'actionAllow' | 'actionAsk' | 'actionDeny'
> = {
  allow: 'actionAllow',
  ask: 'actionAsk',
  deny: 'actionDeny',
};

const DEFAULT_RULE: TerminalApprovalRule = { action: 'allow', pattern: '' };

export function TerminalApprovalRulesDialog({
  isOpen,
  onClose,
}: TerminalApprovalRulesDialogProps) {
  const t = useTranslations('chat.copilot.terminalApproval');
  const tCommon = useTranslations('common');
  const rules = useTerminalApprovalStore((state) => state.rules);
  const addRule = useTerminalApprovalStore((state) => state.addRule);
  const removeRule = useTerminalApprovalStore((state) => state.removeRule);
  const toolRules = useTerminalApprovalStore((state) => state.toolRules);
  const removeToolRule = useTerminalApprovalStore(
    (state) => state.removeToolRule,
  );
  const form = useForm<TerminalApprovalRule>({
    resolver: zodResolver(TerminalApprovalRuleSchema),
    defaultValues: DEFAULT_RULE,
  });
  const pattern = useWatch({ control: form.control, name: 'pattern' });
  const actionLabel = (value: TerminalApprovalRuleAction): string =>
    t(ACTION_LABEL_KEYS[value]);
  const actionOptions = ACTION_ORDER.map((value) => ({
    value,
    label: actionLabel(value),
  }));
  const handleAdd = form.handleSubmit((rule) => {
    if (!addRule(rule)) {
      form.setError('pattern', { type: 'limit' });

      return;
    }

    form.reset({ action: rule.action, pattern: '' });
  });

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      title={t('rulesTitle')}
      description={t('rulesDescription')}
      size="md"
      primaryAction={{ label: tCommon('done'), onClick: onClose }}
    >
      <VStack gap={4} alignItems="stretch">
        <VStack as="form" gap={3} alignItems="stretch" onSubmit={handleAdd}>
          <Controller
            control={form.control}
            name="action"
            render={({ field }) => (
              <SegmentGroupHorizontal
                options={actionOptions}
                value={field.value}
                onValueChange={({ value }) => {
                  const parsed =
                    TerminalApprovalRuleActionSchema.safeParse(value);

                  if (parsed.success) {
                    field.onChange(parsed.data);
                  }
                }}
              />
            )}
          />
          <Field.Root invalid={Boolean(form.formState.errors.pattern)}>
            <Field.Label>{t('patternLabel')}</Field.Label>
            <HStack gap={2} alignItems="flex-start">
              <Input
                size="sm"
                flex={1}
                fontFamily="mono"
                placeholder={t('patternPlaceholder')}
                maxLength={TERMINAL_APPROVAL_RULE_PATTERN_MAX_LENGTH}
                {...form.register('pattern')}
              />
              <Button
                type="submit"
                variant="outline"
                size="sm"
                disabled={!pattern.trim()}
              >
                {tCommon('add')}
              </Button>
            </HStack>
            {form.formState.errors.pattern ? (
              <Field.ErrorText>{t('ruleRejected')}</Field.ErrorText>
            ) : null}
          </Field.Root>
        </VStack>

        {rules.length === 0 && toolRules.length === 0 ? (
          <MetaDefault color="fg.muted">{t('rulesEmpty')}</MetaDefault>
        ) : null}

        {rules.length > 0 ? (
          <VStack gap={1} alignItems="stretch">
            {rules.map((rule) => (
              <TerminalApprovalRuleRow
                key={`${rule.action}:${rule.pattern}`}
                action={rule.action}
                label={rule.pattern}
                actionLabel={actionLabel(rule.action)}
                removeLabel={tCommon('remove')}
                onRemove={() => removeRule(rule)}
              />
            ))}
          </VStack>
        ) : null}

        {toolRules.length > 0 ? (
          <VStack gap={1} alignItems="stretch">
            <LabelDefault color="fg.muted">{t('toolRulesTitle')}</LabelDefault>
            {toolRules.map((rule) => {
              const label = formatToolApprovalRule(rule);

              return (
                <TerminalApprovalRuleRow
                  key={`${rule.action}:${label}`}
                  action={rule.action}
                  label={label}
                  actionLabel={actionLabel(rule.action)}
                  removeLabel={tCommon('remove')}
                  onRemove={() => removeToolRule(rule)}
                />
              );
            })}
          </VStack>
        ) : null}
      </VStack>
    </Dialog>
  );
}
